import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import GradeService from '../../services/grade.service';

const DeletedGradesList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoreDialog, setRestoreDialog] = useState({ open: false, id: null, name: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeletedGrades();
  }, []);

  const fetchDeletedGrades = async () => {
    setLoading(true);
    try {
      const response = await GradeService.getTrashed();
      setGrades(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching deleted grades:', error);
      showSnackbar(t('grades.errorLoadingDeletedGrades') + ': ' + (error.response?.data?.message || error.message), 'error');
      setGrades([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (id, name) => {
    setRestoreDialog({ open: true, id, name });
  };

  const confirmRestore = async () => {
    try {
      await GradeService.restore(restoreDialog.id);
      showSnackbar(t('grades.gradeRestoredSuccess'));
      fetchDeletedGrades();
    } catch (error) {
      showSnackbar(t('grades.errorRestoringGrade'), 'error');
    } finally {
      setRestoreDialog({ open: false, id: null, name: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredGrades = grades.filter(grade =>
    grade.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/grades')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">{t('grades.deletedGrades')}</Typography>
            </Box>
          </Box>

          <TextField
            placeholder={t('grades.searchDeletedPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('grades.name')}</TableCell>
                  <TableCell>{t('grades.deletedAt')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredGrades.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {t('grades.noDeletedGradesFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredGrades.map((grade) => (
                    <TableRow key={grade.id} hover>
                      <TableCell>{grade.name}</TableCell>
                      <TableCell>{formatDate(grade.deleted_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('grades.edit') && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRestore(grade.id, grade.name)}
                              title={t('grades.restoreGrade')}
                            >
                              <RestoreIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={restoreDialog.open}
        onClose={() => setRestoreDialog({ open: false, id: null, name: null })}
      >
        <DialogTitle>{t('grades.restoreGradeTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('grades.restoreGradeConfirm', { name: restoreDialog.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, id: null, name: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmRestore} color="primary" variant="contained">
            {t('grades.restoreGrade')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeletedGradesList;
